import getEnvironment, { EnvironmentTypes } from '@zola-helpers/client/dist/es/util/environment';
import { ApplyFacetOptionsRequest } from '@zola/svc-marketplace-ts-types';

import _concat from 'lodash/concat';
import _get from 'lodash/get';

import Facets from '~/meta/facets';
import Logger from '~/util/logger';

import { compressEventTypes, expandEventTypeGroup } from '../questions/Events/helpers';
import {
  FacetKeysAsFields,
  InquiryFormValues,
  GroupedFormFacets,
  BuildInquiryRequest,
  FormInitializer,
  InquiryFormFacets,
  WeddingEventType,
  IsAnsweredFunction,
} from '../types';

export type FacetOption = { text: string; value: string; displayOrder?: number };

export type SimpleFacetField = {
  grouped: false;
  facetName: keyof InquiryFormFacets;
};
type GroupedFacetField = {
  grouped: true;
  facetName: keyof GroupedFormFacets;
};

export type FieldChoiceProps = SimpleFacetField | GroupedFacetField;

export const convertFormFacet = (
  parentFacetName: string,
  facetKeys: string[] | string | undefined
): ApplyFacetOptionsRequest | undefined => {
  const facetMeta = Facets[parentFacetName as keyof typeof Facets];

  if (facetMeta && facetKeys) {
    return {
      rootFacetId: facetMeta.id,
      // coerces the facet value into a 1-item array if it's not already
      facetKeys: _concat([], facetKeys),

      rootFacetKey: facetMeta.key,
    };
  }

  const message = `Facet not included in meta/facets.js: ${parentFacetName}`;
  if (getEnvironment() === EnvironmentTypes.DEVELOPMENT) {
    throw new Error(message);
  }
  Logger.warn(message);

  return undefined;
};

/**
 * Converts facets in the form to facets in the inquiry create request
 *
 * @param inquiryFormFacets
 * @returns
 */
export const formatFacetOptions = (
  inquiryFormFacets: InquiryFormValues['inquiryFormFacets']
): ApplyFacetOptionsRequest[] => {
  return inquiryFormFacets && Object.keys(inquiryFormFacets || {})
    ? (Object.keys(inquiryFormFacets)
        .map((parentFacetName) => {
          const facetKeys = (inquiryFormFacets as Record<string, string[]>)[parentFacetName];
          return convertFormFacet(parentFacetName, facetKeys);
        })
        .filter(Boolean) as ApplyFacetOptionsRequest[])
    : [];
};

export const getFieldWithPrefix = ({ grouped, facetName }: FieldChoiceProps): FacetKeysAsFields => {
  if (grouped) {
    return `groupedFacets.${facetName}`;
  }
  return `inquiryFormFacets.${facetName}`;
};

/**
 * For a facet, represented as fieldWithPrefix, find the selections the user has
 * made in the form
 *
 * @example
 * getSelectedFacetsFromForm({
 *   groupedFacets: {
 *     EVENT_TYPES: [ 'ENGAGEMENT', 'PRE_WEDDING', 'DAY_OF'],
 *   },
 * }, 'groupedFacets.EVENT_TYPES') => [ 'ENGAGEMENT', 'PRE_WEDDING', 'DAY_OF']
 *
 */
export const getSelectedFacetsFromForm = (
  values: InquiryFormValues,
  fieldWithPrefix: FacetKeysAsFields
) => {
  return _get(values, fieldWithPrefix, []) as string[];
};

export const getFacetErrorField = ({ facetName }: FieldChoiceProps) => `${facetName}Error`;

export const requiredFacetSelectionValidator =
  (props: FieldChoiceProps) => (values: InquiryFormValues) => {
    const { facetName, grouped } = props;
    let selectedOptions: string[] = [];
    if (grouped) {
      selectedOptions = (values.groupedFacets || {})[facetName] || [];
    } else {
      selectedOptions = (values.inquiryFormFacets || {})[facetName] || [];
    }

    if ((selectedOptions || []).length > 0) {
      return undefined;
    }
    return {
      [getFacetErrorField(props)]: 'Required',
    };
  };

export const isFacetQuestionAnswered =
  ({
    grouped,
    facetName,
    required,
  }: FieldChoiceProps & { required: boolean }): IsAnsweredFunction =>
  ({ initialValues }) => {
    const fieldWithPrefix = getFieldWithPrefix({ grouped, facetName } as FieldChoiceProps);

    const selections = getSelectedFacetsFromForm(initialValues, fieldWithPrefix);
    return required ? selections.length > 0 : true;
  };

/**
 * Takes selected facets from the form and adds them to the inquiry request.
 */
export const addSelectedFacetsToInquiry =
  ({ grouped, facetName }: FieldChoiceProps): BuildInquiryRequest =>
  ({ values, inquiry }) => {
    let facetKeys: string[] = [];
    if (grouped) {
      if (facetName === 'EVENT_TYPES') {
        facetKeys = (values.groupedFacets?.EVENT_TYPES || []).flatMap((groupedEventType) =>
          expandEventTypeGroup(groupedEventType)
        ) as string[];
      } else if (getEnvironment() === EnvironmentTypes.DEVELOPMENT) {
        throw new Error(`Unsupported grouped facet: ${facetName}`);
      }
    } else {
      facetKeys = (values.inquiryFormFacets || {})[facetName] || [];
    }
    const converted = convertFormFacet(facetName, facetKeys);
    if (converted && converted.facetKeys.length > 0) {
      return {
        ...inquiry,
        vendorOptions: [...(inquiry.vendorOptions || []), converted],
      };
    }
    return inquiry;
  };

/**
 * Initialize the form for regular facet questions.  For services questions,
 * where we consider what the vendor offers, use initializeServiceFacetFormValues.
 */
export const initializeFacetFormValues =
  ({ grouped, facetName }: FieldChoiceProps): FormInitializer =>
  ({ preferences }, values) => {
    const newValues = { ...values };
    if (grouped) {
      newValues.groupedFacets = newValues.groupedFacets || {};

      if (facetName === 'EVENT_TYPES' && preferences?.inquiryFormFacets?.EVENT_TYPES) {
        newValues.groupedFacets.EVENT_TYPES = compressEventTypes(
          preferences.inquiryFormFacets.EVENT_TYPES as WeddingEventType[]
        );
      }
    } else if (preferences?.inquiryFormFacets?.[facetName]) {
      newValues.inquiryFormFacets = newValues.inquiryFormFacets || {};

      newValues.inquiryFormFacets = {
        ...newValues.inquiryFormFacets,
        [facetName]: preferences?.inquiryFormFacets[facetName],
      } as InquiryFormFacets;
    }
    return newValues;
  };

/**
 * Initialize the form for a services question.  This looks at the services offered
 * by the vendor, and if the couple previously was looking for something that
 * is not offered by this vendor, filters out all the selections, forcing the
 * couple to re-pick for this vendor.
 *
 * For instance:
 * - First inquiry: ask for Cakes and Donuts (fun!)
 * - Second inquiry: Donuts are not offered, so we'll make them re-pick
 *
 * We considered trying to explain that previously, donuts were desired, but
 * donuts are not offered by this vendor, so you need to make sure that you just
 * want to inquiry about cakes, but all the cases to explain that felt too
 * complicated.
 */

export const initializeServiceFacetFormValues =
  (
    facetName: keyof InquiryFormFacets,
    parentFacetKey: ServiceFacet | 'planner-service-level'
  ): FormInitializer =>
  ({ preferences, servicesOffered }, values) => {
    const newValues = { ...values };
    newValues.inquiryFormFacets = newValues.inquiryFormFacets || {};

    if (preferences?.inquiryFormFacets?.[facetName]) {
      const lookingFor = preferences.inquiryFormFacets[facetName];
      const offered = servicesOffered[parentFacetKey] || [];

      let filtered = lookingFor;
      if (offered.length > 0) {
        if (lookingFor.some((facetKey) => offered.findIndex(({ key }) => key === facetKey) < 0)) {
          // If there was any service they previously requested, that is not offered by this
          // particular vendor, reset their selections and make them answer again.
          filtered = [];
        }
      }

      newValues.inquiryFormFacets = {
        ...newValues.inquiryFormFacets,
        [facetName]: filtered,
      };
    }

    return newValues;
  };

/**
 *
 * The facets that are treated at service facets: The couple must pick from what
 * the storefront offers.  Non-service facets allow the couple to pick from any
 * possible choice, regardless of if the vendor offers that choice or not.
 *
 * This does not include the planner-service-level, which is also a service
 * facet but is treated differently.  Perhaps that should be reconsidered
 */
export type ServiceFacet =
  | 'bakers-product-types'
  | 'caterer-drink-types'
  | 'florist-service-level'
  | 'hair-beauty-service-category'
  | 'menu-formats-offered'
  | 'musician-type'
  | 'venue-service-level';
